import { isValidLineData } from './cart.logic';
import type { CartLine, CartState, StoreCart } from './types';

export const CART_STORAGE_KEY = 'cart';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isCartLine(value: unknown): value is CartLine {
  if (!isRecord(value)) return false;
  const snapshot = value.snapshot;
  return (
    isRecord(snapshot) &&
    isValidLineData(value.qty, snapshot.price) &&
    typeof snapshot.nameRu === 'string' &&
    typeof snapshot.unit === 'string' &&
    typeof snapshot.allowFractional === 'boolean' &&
    (snapshot.thumbUrl === null || typeof snapshot.thumbUrl === 'string')
  );
}

/** Drops anything that doesn't look like a cart line — the storage may be stale, hand-edited or
 *  written by an older build, and a bad row must never crash the shop. */
export function parseCartState(raw: unknown): CartState {
  if (!isRecord(raw)) return {};
  const state: CartState = {};
  for (const [storeId, storeCart] of Object.entries(raw)) {
    if (!isRecord(storeCart)) continue;
    const lines: StoreCart = {};
    for (const [productId, line] of Object.entries(storeCart)) {
      if (isCartLine(line)) lines[productId] = line;
    }
    if (Object.keys(lines).length > 0) state[storeId] = lines;
  }
  return state;
}

/** Reads the whole persisted map `userId → carts`; storage may be unavailable or corrupt → empty. */
function readAll(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;
    return isRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

/**
 * Reads one user's persisted carts. Carts are stored per user id, so on a shared device another
 * account never sees them (and an expired session keeps them until the same user signs back in).
 */
export function loadCartState(userId: string): CartState {
  return parseCartState(readAll()[userId]);
}

/** Applies to `stored` only the store carts that differ between `previous` and `next` (Immer keeps
 *  the reference of an untouched store cart, so reference equality is the change test). */
function mergeChangedStores(stored: CartState, next: CartState, previous: CartState): CartState {
  const merged: CartState = { ...stored };
  for (const storeId of new Set([...Object.keys(previous), ...Object.keys(next)])) {
    if (previous[storeId] === next[storeId]) continue;
    const nextCart = next[storeId];
    if (nextCart) merged[storeId] = nextCart;
    else delete merged[storeId];
  }
  return merged;
}

/**
 * Persists one user's carts; an empty state removes that user's entry (and the key when unused).
 *
 * With `previous` (the carts this tab last saw/wrote) only the stores that changed since then are
 * written on top of what is currently in storage, so two open tabs don't overwrite each other's
 * changes to *different* stores. Without it the user's entry is replaced wholesale.
 */
export function saveCartState(userId: string, state: CartState, previous?: CartState): void {
  try {
    const all = readAll();
    const next = previous ? mergeChangedStores(parseCartState(all[userId]), state, previous) : state;
    if (Object.keys(next).length === 0) delete all[userId];
    else all[userId] = next;
    if (Object.keys(all).length === 0) localStorage.removeItem(CART_STORAGE_KEY);
    else localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(all));
  } catch {
    // Quota exceeded / storage blocked — the cart just stays in memory for this session.
  }
}
